import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useDojos } from '@/lib/queries';
import { date, dateTime, gradeLabel, money, instructorRole } from '@/lib/format';
import { adminLinks } from '@/lib/links';
import { ErrorNote, Spinner } from '@/components/Layout';

interface Entry {
  id: number; at: string; actor_name: string; actor_kind: 'admin' | 'instructor' | 'member' | 'system'; table_name: string; record_id: string; action: 'insert' | 'update' | 'delete';
  label: string | null; changes: Record<string, unknown>; person_id: string | null; household_id: string | null; dojo_id: string | null; event_id: string | null;
}

const NOUN: Record<string, string> = {
  person: 'details', household: 'household', membership: 'membership', payment: 'payment', payment_method: 'Direct Debit mandate', subscription: 'auto-renewal',
  dojo: 'dojo', dojo_instructor: 'instructor record', app_admin: 'admin access', membership_plan: 'membership price',
  event: 'event', event_option: 'event price option', event_registration: 'event booking', grading: 'grading',
  email_template: 'email template', email_batch: 'email', refund: 'refund',
};
const FIELD: Record<string, string> = {
  grade_rank: 'Grade', for_rank: 'Grading for', from_rank: 'Grading from', home_dojo_id: 'Dojo', dojo_id: 'Dojo', host_dojo_id: 'Host dojo', date_of_birth: 'Date of birth',
  dbs_expires_on: 'DBS renewal due', dbs_issued_on: 'DBS issued', dbs_certificate_number: 'DBS certificate number', dbs_update_service: 'DBS Update Service',
  insurance_expires_on: 'Insurance runs out', recommended: 'Sensei approved', recommended_by: 'Approved by', photo_path: 'Photo', is_household_manager: 'Account holder',
  user_id: 'Login', emergency_contact_name: 'Emergency contact', emergency_contact_phone: 'Emergency phone', starts_at: 'Starts', ends_at: 'Ends', body_md: 'Page text',
  frozen_at: 'Membership paused', frozen_reason: 'Freeze message', first_aid_expires_on: 'First aid runs out', declaration_signed_on: 'Declaration signed',
  allow_pay_on_day: 'Pay on the day allowed', pay_on_day: 'Due on the day', fee_payment_id: 'Grading fee payment', fee_pence: 'Grading fee', fee_paid: 'Grading fee paid',
  reference: 'Reference', note: 'Note', provider: 'Payment method', method: 'Refund method', collected_by_person_id: 'Collected by', member_number: 'Member number', role: 'Role',
  sent_as: 'Sent as', reply_to: 'Replies to', for_instructors: 'Instructors can use', discount_pence: 'Discount', discount_reason: 'Discount reason',
  parent_payment_id: 'Balance payment of', checked_in_at: 'Checked in', desk_note: 'Desk note', member_code: 'Member code', refunded_on: 'Refunded on', amount_pence: 'Amount', paypal_payer_id: 'PayPal payer', charge_date: 'Date received',
};
const HIDE = new Set(['id', 'checked_in_by', 'recommended_at', 'recommended_by_person', 'provider_payment_id', 'checkout_ref', 'gocardless_customer_id',
  'frozen_by', 'recorded_by', 'updated_by', 'sent_by', 'provider_refund_id', 'paypal_payer_id']);
const KIND: Record<Entry['actor_kind'], string> = { admin: 'admin', instructor: 'instructor', member: 'member', system: 'automatic' };

const label = (k: string) => FIELD[k] ?? (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, ' ').replace(/ id$/, '');

/** One value as a person would read it. */
function show(k: string, v: unknown, dojoName: (id: string) => string | undefined): string {
  if (v === null || v === undefined || v === '') return '(blank)';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (k.endsWith('_rank') && typeof v === 'number') return gradeLabel(v);
  if (k.endsWith('_pence') && typeof v === 'number') return money(v);
  if (k.endsWith('dojo_id') && typeof v === 'string') return dojoName(v) ?? 'another dojo';
  if (k === 'photo_path') return 'new photo';
  if (k === 'member_code') return 'new code';
  if (k === 'role' && (v === 'chief' || v === 'assistant')) return instructorRole(v);
  if (k.endsWith('_id') || k === 'user_id') return 'changed';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return date(s);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return dateTime(s);
  return s.length > 120 ? `${s.slice(0, 120)}…` : s.replace(/_/g, ' ');
}

/** Where an entry's record lives in the admin area. */
function recordLink(e: Entry): string | null {
  if (e.table_name === 'event' || e.table_name === 'event_option') return e.event_id && adminLinks.event(e.event_id);
  if (e.table_name === 'dojo') return adminLinks.dojo(e.record_id);
  if (e.table_name === 'household' || (!e.person_id && e.household_id)) return e.household_id && adminLinks.household(e.household_id);
  if (e.person_id) return adminLinks.person(e.person_id);
  if (e.dojo_id) return adminLinks.dojo(e.dojo_id);
  return null;
}

function EntryRow({ e, dojoName, showRecord }: { e: Entry; dojoName: (id: string) => string | undefined; showRecord: boolean }) {
  const noun = NOUN[e.table_name] ?? e.table_name.replace(/_/g, ' ');
  const what = e.label ? `${e.label}: ${noun}` : noun;
  const link = showRecord ? recordLink(e) : null;
  const changed = e.action === 'update' ? Object.entries(e.changes as Record<string, [unknown, unknown]>).filter(([k]) => !HIDE.has(k)) : [];
  if (e.action === 'update' && !changed.length) return null;
  return (
    <li className="py-2.5">
      <p>
        <span className="font-medium">{e.action === 'insert' ? 'Added' : e.action === 'delete' ? 'Removed' : 'Changed'} {link ? <Link to={link} className="link">{what}</Link> : what}</span>
        <span className="text-ink-500"> · {e.actor_name}{e.actor_kind !== 'member' && ` (${KIND[e.actor_kind]})`} · {dateTime(e.at)}</span>
      </p>
      {changed.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-ink-700">
          {changed.map(([k, [from, to]]) => <li key={k}><span className="text-ink-500">{label(k)}:</span> {show(k, from, dojoName)} → <strong className="font-semibold">{show(k, to, dojoName)}</strong></li>)}
        </ul>
      )}
    </li>
  );
}

/**
 * Change history from the audit log: every edit to the record and to the records
 * under it (a person's memberships and gradings, a dojo's instructors, an event's bookings).
 * With no filter it is the whole association's recent history.
 */
export function History({ by, id, records, title = 'History', limit = 100 }: { by?: 'person_id' | 'household_id' | 'dojo_id' | 'event_id'; id?: string; records?: string[]; title?: string; limit?: number }) {
  const scoped = !!by || !!records;
  const [open, setOpen] = useState(!scoped);
  const dojos = useDojos();
  const dojoName = (d: string) => { const x = dojos.data?.find(y => y.id === d); return x ? x.short_name ?? x.name : undefined; };
  const q = useQuery({
    queryKey: ['audit', by, id, records?.join(), limit],
    enabled: open,
    queryFn: async () => {
      let req = supabase.from('audit_log').select('*').order('at', { ascending: false }).limit(limit);
      if (by && id) req = req.eq(by, id);
      if (records?.length) req = req.in('record_id', records);
      const { data, error } = await req;
      if (error) throw error;
      return data as Entry[];
    },
  });
  return (
    <section className="card space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">{title}</h3>
        {scoped && <button className="btn-ghost text-sm" onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</button>}
      </div>
      {open && (q.isLoading ? <Spinner /> : q.error ? <ErrorNote>Could not load the history.</ErrorNote>
        : q.data?.length ? <ul className="divide-y divide-stone-100 text-sm">{q.data.map(e => <EntryRow key={e.id} e={e} dojoName={dojoName} showRecord={!scoped || (!!by && e.table_name !== by.replace('_id', '')) || (!!records && e.record_id !== records[0])} />)}</ul>
        : <p className="text-sm text-ink-500">No changes recorded yet. History starts from 21 October 2026.</p>)}
    </section>
  );
}
